import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { syncProductBadgeRootsFromSectionHtml } from '@theme/sync-product-badge';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /** @type {{ img: HTMLImageElement; src: string; srcset: string | null } | null} */
  #cardGalleryPreviewBackup = null;

  /** @type {{ slideId: string; hidden: boolean; variantImage: string | null; imgSrc: string; imgSrcset: string | null }[] | null} */
  #cardGallerySlidesBaseline = null;

  /** @type {boolean} */
  #cardGalleryHoverActive = false;

  /** @type {boolean} */
  #cardGallerySwatchHoverActive = false;

  /** @type {HTMLInputElement | null} */
  #cardGalleryHoveredInput = null;

  /** @type {string} */
  #cardGalleryConnectedPayload = '';

  /** @type {Promise<void> | null} */
  #connectedGalleryApplyPromise = null;

  /** @type {Promise<void>} */
  #connectedGalleryApplyChain = Promise.resolve();

  /** @type {number} */
  #connectedGalleryApplyGeneration = 0;


  #ensureCardGalleryBaselineCaptured() {
    if (this.#cardGallerySlidesBaseline !== null) return;
    const slides = this.#resolveSlideshow()?.refs?.slides ?? [];
    this.#cardGallerySlidesBaseline = slides.map((slide) => {
      const img = slide.querySelector('img');
      const v = slide.getAttribute('variant-image');
      return {
        slideId: slide.getAttribute('slide-id') ?? '',
        hidden: slide.hidden,
        variantImage: v,
        imgSrc: img?.currentSrc || img?.src || '',
        imgSrcset: img?.getAttribute('srcset') ?? null,
      };
    });
  }

  #removeInjectedConnectedSlides() {
    const scroller = this.#resolveSlideshow()?.refs?.scroller;
    if (!scroller) return;
    for (const el of scroller.querySelectorAll('[data-connected-injected-slide]')) {
      el.remove();
    }
  }

  #restoreListingCardGallerySlides() {
    const baseline = this.#cardGallerySlidesBaseline;
    const slideshow = this.#resolveSlideshow();
    const scroller = slideshow?.refs?.scroller;
    if (!scroller || !baseline?.length) return;

    this.#removeInjectedConnectedSlides();

    const slides = slideshow.refs?.slides ?? [];
    for (let i = 0; i < baseline.length; i++) {
      const slide = slides[i];
      const b = baseline[i];
      if (!slide || !b) continue;
      slide.setAttribute('slide-id', b.slideId);
      slide.hidden = b.hidden;
      if (b.variantImage != null && b.variantImage !== '') slide.setAttribute('variant-image', b.variantImage);
      else slide.removeAttribute('variant-image');
      const img = slide.querySelector('img');
      if (img && b.imgSrc) {
        img.src = b.imgSrc;
        if (b.imgSrcset) img.setAttribute('srcset', b.imgSrcset);
        else img.removeAttribute('srcset');
      }
    }

    const extraSlides = Array.from(scroller.querySelectorAll('slideshow-slide')).slice(baseline.length);
    for (const slide of extraSlides) {
      slide.remove();
    }

    if (typeof slideshow.updatedCallback === 'function') {
      slideshow.updatedCallback();
    }

    const slideCount = slideshow.slides?.length ?? 0;
    if (slideCount && slideshow.current >= slideCount) {
      slideshow.current = 0;
    }

    this.#connectedGalleryApplyGeneration++;
    this.#cardGalleryConnectedPayload = '';
  }

  /**
   * @param {string} payload
   * @param {string | undefined} [featuredMediaId]
   * @returns {boolean} Whether the gallery DOM rebuild was scheduled
   */
  #syncConnectedCardGalleryPayload(payload, featuredMediaId) {
    if (!payload) return false;
    if (payload === this.#cardGalleryConnectedPayload) return false;

    const generation = ++this.#connectedGalleryApplyGeneration;
    const run = this.#connectedGalleryApplyChain.then(() =>
      this.#applyConnectedChildCardGalleryPayload(payload, featuredMediaId, generation)
    );
    this.#connectedGalleryApplyChain = run.catch(() => {});
    this.#connectedGalleryApplyPromise = run;
    return true;
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {HTMLElement | null}
   */
  #resolveGalleryScroller(slideshow) {
    const fromRefs = slideshow.refs?.scroller;
    if (fromRefs instanceof HTMLElement) return fromRefs;

    const fromQuery = slideshow.querySelector('[ref="scroller"]');
    if (fromQuery instanceof HTMLElement) return fromQuery;

    const slide = slideshow.querySelector('slideshow-slide');
    if (slide?.parentElement instanceof HTMLElement) return slide.parentElement;

    return null;
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {HTMLElement[]}
   */
  #getGallerySlides(slideshow) {
    const scroller = this.#resolveGalleryScroller(slideshow);
    if (!scroller) return [];

    return Array.from(scroller.querySelectorAll('slideshow-slide')).filter(
      (slide) => slide instanceof HTMLElement && !slide.hidden
    );
  }

  /** @param {string} payload @param {string | undefined} [featuredMediaId] @param {number} [generation] @returns {Promise<void>} */
  async #applyConnectedChildCardGalleryPayload(payload, featuredMediaId, generation = this.#connectedGalleryApplyGeneration) {
    const slideshow = this.#resolveSlideshow();
    if (!slideshow || !payload) return;

    if (typeof slideshow.whenInitComplete === 'function') {
      await slideshow.whenInitComplete();
    }

    let scroller = this.#resolveGalleryScroller(slideshow);
    for (let i = 0; i < 5 && !scroller; i++) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      scroller = this.#resolveGalleryScroller(slideshow);
    }

    if (!scroller || generation !== this.#connectedGalleryApplyGeneration) return;

    this.#removeInjectedConnectedSlides();

    /** @type {{ id: string; src: string }[]} */
    const items = [];
    for (const seg of payload.split('%%')) {
      const sep = seg.indexOf('::');
      if (sep === -1) continue;
      const id = seg.slice(0, sep);
      const src = seg.slice(sep + 2);
      if (id && src) items.push({ id, src });
    }
    const connectedGalleryMax = 6;
    if (items.length > connectedGalleryMax) {
      items.splice(connectedGalleryMax);
    }
    if (!items.length) return;

    if (featuredMediaId) {
      const featuredIndex = items.findIndex((item) => item.id === String(featuredMediaId));
      const featuredItem = featuredIndex > 0 ? items[featuredIndex] : undefined;
      if (featuredItem) {
        items.splice(featuredIndex, 1);
        items.unshift(featuredItem);
      }
    }

    const template = scroller.querySelector('slideshow-slide:not([data-connected-injected-slide])');
    if (!(template instanceof HTMLElement)) return;

    while (scroller.querySelectorAll('slideshow-slide').length < items.length) {
      const clone = /** @type {HTMLElement} */ (template.cloneNode(true));
      clone.setAttribute('data-connected-injected-slide', 'true');
      clone.removeAttribute('variant-image');
      scroller.appendChild(clone);
    }

    let slideEls = Array.from(scroller.querySelectorAll('slideshow-slide'));
    while (slideEls.length > items.length) {
      const injected = scroller.querySelector('slideshow-slide[data-connected-injected-slide]');
      const toRemove =
        injected instanceof HTMLElement ? injected : slideEls[slideEls.length - 1];
      if (!(toRemove instanceof HTMLElement)) break;
      toRemove.remove();
      slideEls = Array.from(scroller.querySelectorAll('slideshow-slide'));
    }

    slideEls = Array.from(scroller.querySelectorAll('slideshow-slide'));
    for (let i = 0; i < slideEls.length; i++) {
      const slide = slideEls[i];
      if (!slide) continue;
      const item = items[i];
      const img = slide.querySelector('img');
      if (!item) {
        slide.hidden = true;
        slide.removeAttribute('variant-image');
        continue;
      }
      slide.hidden = false;
      slide.removeAttribute('variant-image');
      slide.setAttribute('slide-id', item.id);
      if (img) {
        img.removeAttribute('srcset');
        img.src = item.src;
      }
    }

    if (typeof slideshow.updatedCallback === 'function') {
      slideshow.updatedCallback();
    }

    if (typeof slideshow.refreshSlideObservers === 'function') {
      slideshow.setAttribute('initial-slide', '0');
      await slideshow.refreshSlideObservers();
    }

    if (generation !== this.#connectedGalleryApplyGeneration) return;

    this.#cardGalleryConnectedPayload = payload;
  }

  /**
   * @param {import('slideshow').Slideshow} slideshow
   * @param {string} mediaId
   * @returns {HTMLElement | null}
   */
  #findSlideshowSlideByMediaId(slideshow, mediaId) {
    if (!slideshow) return null;

    const id = String(mediaId);
    const scroller = this.#resolveGalleryScroller(slideshow);
    if (scroller) {
      for (const slide of scroller.querySelectorAll('slideshow-slide')) {
        if (!(slide instanceof HTMLElement)) continue;
        if (slide.getAttribute('slide-id') === id) return slide;
      }
    }

    return this.#getGallerySlides(slideshow).find((s) => s.getAttribute('slide-id') === id) ?? null;
  }

  /** @returns {import('slideshow').Slideshow | null} */
  #resolveSlideshow() {
    return (
      this.refs.slideshow ??
      /** @type {import('slideshow').Slideshow | null} */ (this.querySelector('slideshow-component'))
    );
  }

  /**
   * Card "second image on hover" reads gallery datasets from the visible swatch row. Split layouts
   * duplicate swatches inside `.options-block__main-picker--hide-swatch-fieldsets` (separate form);
   * that hidden row can stay checked with stale URLs while the shopper uses `product-swatches`.
   * @returns {HTMLInputElement | undefined}
   */
  #cardGallerySwatchSourceInput() {
    /** @type {HTMLInputElement | undefined} */
    let last;

    for (const el of this.querySelectorAll('fieldset.variant-option--swatches input[type="radio"]:checked')) {
      if (!(el instanceof HTMLInputElement)) continue;
      if (el.closest('.options-block__main-picker--hide-swatch-fieldsets')) continue;
      if (el.closest('[data-erp-masked-option="true"]')) continue;
      last = el;
    }

    return last;
  }

  /** @type {number} */
  #galleryUpdateGeneration = 0;

  /** @type {boolean} */
  #variantImagesUpdateScheduled = false;

  /** @type {Array<(() => void) | undefined>} */
  #variantImagesUpdateCallbacks = [];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const quickAdd = this.querySelector('quick-add-component');
    const quickIdField = quickAdd?.querySelector('form input[name="id"]');
    if (
      quickIdField instanceof HTMLInputElement &&
      !quickIdField.disabled &&
      quickIdField.value
    ) {
      return quickIdField.value;
    }

    // If the merchant explicitly requires the shopper to pick a swatch first,
    // never return a default/pre-populated id from the quick-add hidden input
    // or from the product card link href - the gate must hold until a radio
    // is actually selected.
    if (quickAdd?.getAttribute('data-require-explicit-swatch') === 'true') {
      return null;
    }

    // The first :checked [data-variant-id] in DOM is often the *first* tier
    // (e.g. size), whose `data-variant-id` is a partial / first-swatch match.
    // Prefer the checked radio with the highest `data-input-id` option position.
    const tiered = this.querySelectorAll(
      'input[type="radio"]:checked[data-variant-id][data-input-id]',
    );
    if (tiered.length) {
      let best = /** @type {HTMLInputElement | null} */ (null);
      let bestPos = -1;
      for (const el of tiered) {
        if (!(el instanceof HTMLInputElement) || !el.dataset.variantId) continue;
        const raw = el.dataset.inputId;
        if (!raw) continue;
        const pos = parseInt(String(raw).split('-')[0] || '0', 10) - 1;
        if (Number.isNaN(pos)) continue;
        if (pos > bestPos) {
          bestPos = pos;
          best = el;
        }
      }
      if (best?.dataset.variantId) {
        return best.dataset.variantId;
      }
    }

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    if (checkedInput?.dataset.variantId) {
      return checkedInput.dataset.variantId;
    }

    const link = this.refs.productCardLink;
    if (link instanceof HTMLAnchorElement && link.href) {
      try {
        const url = new URL(link.href, window.location.origin);
        const variantParam = url.searchParams.get('variant');
        if (variantParam) {
          return variantParam;
        }
      } catch {
        // ignore invalid URL
      }
    }

    return null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    if (this.refs.quickAdd?.dataset.inlineQuickAdd === 'true' && this.refs.quickAdd?.dataset.mobileModalQuickAdd !== 'true') return;
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  #mobilePrefetchHandler = () => {
    // Disabled: mobile prefetch causes 429 rate limit errors due to concurrent section renders
  };

  /** @type {string} */
  #optionsInstanceSuffix = '';

  /** @returns {string} */
  #resolveOptionsInstanceSuffix() {
    if (this.#optionsInstanceSuffix) return this.#optionsInstanceSuffix;

    let instanceId = this.dataset.optionsInstanceId;
    if (!instanceId) {
      const gridItem = this.closest('.product-grid__item');
      if (gridItem?.id) {
        instanceId = gridItem.id;
      } else {
        const section = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') ?? '';
        const cardShellId = this.id ?? '';
        const productId = this.dataset.productId ?? '';
        instanceId = [section, cardShellId, productId].filter(Boolean).join('-');
        if (!instanceId) {
          instanceId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : `pci-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        }
      }
      this.dataset.optionsInstanceId = instanceId;
    }

    this.#optionsInstanceSuffix = `-inst-${instanceId}`;
    return this.#optionsInstanceSuffix;
  }

  #scopeOptionControlNames() {
    const suffix = this.#resolveOptionsInstanceSuffix();
    const instMarker = '-inst-';

    for (const input of this.querySelectorAll('input[type="radio"][name]')) {
      const name = input.getAttribute('name');
      if (!name || name.includes(instMarker)) continue;
      input.setAttribute('name', `${name}${suffix}`);
    }

    for (const select of this.querySelectorAll('select[id^="Option-"]')) {
      const id = select.id;
      if (!id || id.includes(instMarker)) continue;
      const newId = `${id}${suffix}`;
      const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(id) : id;
      for (const label of this.querySelectorAll(`label[for="${esc}"]`)) {
        label.setAttribute('for', newId);
      }
      select.id = newId;
    }
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#scopeOptionControlNames();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    queueMicrotask(() => this.#syncInitialCardGallery());
  }

  #syncInitialCardGallery() {
    const hasGallery = this.querySelector('.card-gallery');
    if (!hasGallery) return;

    const finish = () => {
      if (this.#resolveSlideshow()?.isNested) {
        this.#preloadNextPreviewImage();
      }
    };

    if (!this.#cardGallerySwatchSourceInput()) {
      finish();
      return;
    }

    this.#updateVariantImages(finish);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const slideshow = this.#resolveSlideshow();
    const currentSlide = slideshow?.slides?.[slideshow.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    this.removeEventListener('touchstart', this.#mobilePrefetchHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler, { once: true });
      this.addEventListener('focusin', this.#fetchProductPageHandler, { once: true });
    } else {
      this.addEventListener('touchstart', this.#mobilePrefetchHandler, { once: true, passive: true });
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const optionValueId = String(event.detail.resource.id ?? '');
    if (!optionValueId) return;

    const swatches = this.variantPicker;
    const mainPicker = /** @type {VariantPicker | null} */ (this.querySelector('variant-picker'));
    const esc = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(optionValueId) : optionValueId;

    if (event.target !== swatches) {
      if (swatches?.querySelector(`[data-option-value-id="${esc}"]`)) {
        swatches.updateSelectedOption(optionValueId);
      }
    } else if (mainPicker?.querySelector(`[data-option-value-id="${esc}"]`)) {
      mainPicker.updateSelectedOption(optionValueId);
    }

    this.#updateVariantImages();
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.#updateProductUrl(event);
    this.#updateProductTitleFromSection(event.detail.data.html);
    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);

    const swatchesPicker = /** @type {VariantPicker | null} */ (
      this.querySelector('swatches-variant-picker-component')
    );
    const mainVariantPicker = /** @type {VariantPicker | null} */ (this.querySelector('variant-picker'));
    const { html } = event.detail.data;
    if (event.target !== swatchesPicker) {
      swatchesPicker?.updateVariantPicker(html);
    }
    if (event.target !== mainVariantPicker) {
      mainVariantPicker?.updateVariantPicker(html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    this.#syncProductBadge(event);
    this.#scopeOptionControlNames();

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();

    if (isDesktopBreakpoint()) {
      this.#fetchProductPageHandler();
    }
    // Mobile: no prefetch on variant update to avoid 429s
  };

  /**
   * Syncs the product badge pill (sold / sale / custom metafield) from section-rendered card HTML.
   * @param {VariantUpdateEvent} event
   */
  #syncProductBadge(event) {
    const html = event.detail.data.html;
    if (!html) return;

    /** @type {HTMLElement[]} */
    const roots = [];
    for (const el of this.querySelectorAll('[data-product-badge-root]')) {
      if (el instanceof HTMLElement) roots.push(el);
    }
    if (!roots.length) return;

    const synced = syncProductBadgeRootsFromSectionHtml(html, roots);
    if (!synced) return;

    const { payload, customText } = synced;
    const gallery = this.refs.cardGallery;
    if (gallery instanceof HTMLElement) {
      gallery.classList.remove('card-gallery--badge-top-left', 'card-gallery--badge-top-right');
      if (payload.soldOut || payload.sale || customText) {
        const corner =
          gallery.dataset.cardGalleryBadgePosition === 'top-right' ? 'top-right' : 'top-left';
        gallery.classList.add(`card-gallery--badge-${corner}`);
      }
    }
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    requestAnimationFrame(() => {
      for (const el of this.querySelectorAll('swatches-variant-picker-component overflow-options, variant-picker overflow-options')) {
        const refresh = /** @type {any} */ (el)?.refresh;
        if (typeof refresh === 'function') refresh.call(el);
      }

      const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
      if (!overflowList?.querySelector('[slot="overflow"]')) return;

      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const html = event.detail.data.html;
    if (!html) {
      return;
    }

    const escapeAttr =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? /** @param {string} s */ (s) => CSS.escape(s)
        : /** @param {string} s */ (s) => s;

    const sourceFallback = html.querySelector('product-price');
    const priceEls = this.querySelectorAll('product-price');

    for (const el of priceEls) {
      if (!(el instanceof HTMLElement)) continue;

      const priceContainer = el.querySelector('[ref="priceContainer"]');
      if (!priceContainer) {
        continue;
      }

      const blockId = el.dataset.blockId;
      let sourceProductPrice =
        blockId != null && blockId !== ''
          ? html.querySelector(`product-price[data-block-id="${escapeAttr(blockId)}"]`)
          : null;
      if (!sourceProductPrice) {
        sourceProductPrice = sourceFallback;
      }

      const newContainer = sourceProductPrice?.querySelector('[ref="priceContainer"]');
      if (!newContainer) {
        continue;
      }

      try {
        morph(priceContainer, /** @type {Element} */ (newContainer.cloneNode(true)));
      } catch (error) {
        console.warn('product-card: price morph failed', error);
      }
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const html = event.detail.data.html;
    const anchorElement = html?.querySelector('product-card a');
    const featuredMediaUrl = html?.querySelector('product-card-link')?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    if (!(productCardLink instanceof HTMLAnchorElement)) {
      return;
    }

    const resource = event.detail.resource;
    const { newProduct } = event.detail.data;

    const gallerySource = this.#cardGallerySwatchSourceInput();
    const checkedMainPicker = /** @type {HTMLInputElement | null} */ (
      this.querySelector('variant-picker input[type="radio"]:checked') ?? null
    );
    const connectedPath =
      gallerySource?.dataset.connectedProductUrl?.trim() ||
      checkedMainPicker?.dataset.connectedProductUrl?.trim();

    /** @type {string | null} */
    let productUrl = null;

    if (newProduct?.url) {
      try {
        const url = new URL(newProduct.url, window.location.origin);
        if (resource?.id != null) url.searchParams.set('variant', String(resource.id));
        productUrl = url.toString();
      } catch {
        productUrl = null;
      }
    }

    if (!productUrl && connectedPath && resource?.id != null) {
      try {
        const url = new URL(connectedPath, window.location.origin);
        url.searchParams.set('variant', String(resource.id));
        productUrl = url.toString();
      } catch {
        productUrl = null;
      }
    }

    if (!productUrl && resource?.id != null) {
      try {
        const url = new URL(productCardLink.href, window.location.origin);
        url.searchParams.set('variant', String(resource.id));
        productUrl = url.toString();
      } catch {
        productUrl = null;
      }
    }

    if (!productUrl && anchorElement instanceof HTMLAnchorElement) {
      productUrl = anchorElement.href;
    }

    if (!productUrl) {
      return;
    }

    productCardLink.href = productUrl;
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = productUrl;
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = productUrl;
    }
  }

  /**
   * @param {Document | ParentNode | null | undefined} html
   */
  #updateProductTitleFromSection(html) {
    if (!html) {
      return;
    }

    const sourceCard = html.querySelector('product-card');
    const syncTitle = sourceCard?.getAttribute('data-sync-product-title');
    if (syncTitle == null || syncTitle === '') {
      return;
    }

    const { productTitleLink } = this.refs;
    if (!(productTitleLink instanceof HTMLElement)) {
      return;
    }

    const textRoot = productTitleLink.firstElementChild;
    if (!(textRoot instanceof HTMLElement)) {
      productTitleLink.textContent = syncTitle;
      return;
    }

    const paragraph = document.createElement('p');
    paragraph.textContent = syncTitle;
    textRoot.replaceChildren(paragraph);
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  #isSpuriousGalleryPointerLeave(event) {
    if (!(event instanceof PointerEvent)) return false;
    const gallery = this.refs.cardGallery;
    const related = event.relatedTarget;
    if (gallery instanceof Element) {
      if (related instanceof Node && gallery.contains(related)) return true;
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      if (hit instanceof Node && gallery.contains(hit)) return true;
    }
    if (
      event.currentTarget instanceof Element &&
      related instanceof Node &&
      event.currentTarget.contains(related)
    ) {
      return true;
    }
    return false;
  }

  /**
   * Keep featured media markup consistent at rest: paint `previewImageSrc` onto the first
   * visible slide (and featured slide if different), and align `slide-id` to `optionMediaId`.
   * Does not navigate the slideshow — callers that need rest index must select explicitly.
   * @param {HTMLInputElement} selectedOption
   */
  #forceFeaturedAtRest(selectedOption) {
    const mediaId = selectedOption.dataset.optionMediaId?.trim();
    const previewSrc = selectedOption.dataset.previewImageSrc?.trim();
    if (!mediaId && !previewSrc) return;

    const slideshow = this.#resolveSlideshow();
    if (!slideshow) return;

    const slides = this.#getGallerySlides(slideshow);
    const featuredSlide =
      (mediaId && slides.find((slide) => slide.getAttribute('slide-id') === mediaId)) || slides[0];
    const visibleSlide = slides[0] ?? featuredSlide;

    const applyPreview = (/** @type {HTMLElement | null | undefined} */ slide) => {
      if (!(slide instanceof HTMLElement) || !previewSrc) return;
      const img = slide.querySelector('img');
      if (!(img instanceof HTMLImageElement)) return;
      img.removeAttribute('srcset');
      img.src = previewSrc;
    };

    applyPreview(visibleSlide);
    if (featuredSlide !== visibleSlide) {
      applyPreview(featuredSlide);
    }

    if (mediaId && featuredSlide instanceof HTMLElement) {
      featuredSlide.setAttribute('slide-id', mediaId);
    }
  }

  /**
   * @param {HTMLInputElement} selectedOption
   * @param {boolean} payloadChanged
   * @param {(() => void) | undefined} onComplete
   */
  async #scheduleConnectedGalleryFeaturedSelect(selectedOption, payloadChanged, onComplete) {
    if (payloadChanged && this.#connectedGalleryApplyPromise) {
      await this.#connectedGalleryApplyPromise;
    }

    const slideshow = this.#resolveSlideshow();
    if (slideshow?.whenInitComplete) {
      await slideshow.whenInitComplete();
    }

    if (payloadChanged) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    if (payloadChanged && !this.#cardGalleryConnectedPayload) {
      const payload = (selectedOption.dataset.connectedCardGallery || '').trim();
      const featuredMediaId = selectedOption.dataset.optionMediaId?.trim();
      if (payload) {
        this.#syncConnectedCardGalleryPayload(payload, featuredMediaId);
        if (this.#connectedGalleryApplyPromise) {
          await this.#connectedGalleryApplyPromise;
        }
      }
    }

    await this.#finalizeVariantImageSlideSelection(true, selectedOption);
    this.#forceFeaturedAtRest(selectedOption);

    const slideshowAfterUpdate = this.#resolveSlideshow();
    if (slideshowAfterUpdate?.refreshSlideObservers) {
      await slideshowAfterUpdate.refreshSlideObservers({ preserveSelection: true });
    }

    onComplete?.();
  }

  /**
   * @param {() => void} selectMedia
   * @param {boolean} payloadChanged
   */
  #afterConnectedGalleryPayload(selectMedia, payloadChanged) {
    if (!payloadChanged) {
      selectMedia();
      return;
    }

    const slideshow = this.#resolveSlideshow();
    void (async () => {
      if (slideshow?.whenInitComplete) {
        await slideshow.whenInitComplete();
      }
      requestAnimationFrame(selectMedia);
    })();
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * @param {(() => void)=} [onComplete]
   */
  #updateVariantImages(onComplete) {
    if (this.#cardGalleryHoverActive) {
      onComplete?.();
      return;
    }

    if (onComplete) this.#variantImagesUpdateCallbacks.push(onComplete);

    if (this.#variantImagesUpdateScheduled) return;
    this.#variantImagesUpdateScheduled = true;

    queueMicrotask(() => {
      this.#variantImagesUpdateScheduled = false;
      const callbacks = this.#variantImagesUpdateCallbacks.splice(0);
      void this.#runUpdateVariantImages(() => {
        for (const callback of callbacks) callback?.();
      });
    });
  }

  /** @param {(() => void) | undefined} onComplete */
  async #runUpdateVariantImages(onComplete) {
    const generation = ++this.#galleryUpdateGeneration;
    const slideshow = this.#resolveSlideshow();
    const selectedOption = this.#cardGallerySwatchSourceInput();
    if (!slideshow || !selectedOption) {
      onComplete?.();
      return;
    }

    if (typeof slideshow.whenInitComplete === 'function') {
      await slideshow.whenInitComplete();
    }
    if (generation !== this.#galleryUpdateGeneration) {
      onComplete?.();
      return;
    }

    this.#discardCardGalleryPreviewBackup();
    this.#ensureCardGalleryBaselineCaptured();

    const connected = (selectedOption.dataset.connectedProductUrl || '').trim();
    const payload = (selectedOption.dataset.connectedCardGallery || '').trim();
    const useConnectedGallery = Boolean(connected && payload);
    const featuredMediaId = selectedOption.dataset.optionMediaId?.trim();
    const scroller = this.#resolveGalleryScroller(slideshow);
    const baselineLen = this.#cardGallerySlidesBaseline?.length ?? 0;

    if (useConnectedGallery) {
      const payloadChanged = this.#syncConnectedCardGalleryPayload(payload, featuredMediaId);
      await this.#scheduleConnectedGalleryFeaturedSelect(selectedOption, payloadChanged, undefined);
      onComplete?.();
      return;
    }

    if (!useConnectedGallery) {
      this.#connectedGalleryApplyGeneration++;
      if (this.#connectedGalleryApplyPromise) {
        await this.#connectedGalleryApplyPromise;
      }
    }
    if (generation !== this.#galleryUpdateGeneration) {
      onComplete?.();
      return;
    }

    const needsRestore =
      Boolean(this.#cardGalleryConnectedPayload) ||
      Boolean(scroller?.querySelector('[data-connected-injected-slide]')) ||
      (slideshow.refs?.slides?.length ?? 0) > baselineLen;

    if (needsRestore) {
      this.#restoreListingCardGallerySlides();
    }
    if (generation !== this.#galleryUpdateGeneration) {
      onComplete?.();
      return;
    }

    await this.#finalizeVariantImageSlideSelection(false, selectedOption);
    if (generation !== this.#galleryUpdateGeneration) {
      onComplete?.();
      return;
    }

    this.#forceFeaturedAtRest(selectedOption);

    const slideshowAfterUpdate = this.#resolveSlideshow();
    if (slideshowAfterUpdate?.refreshSlideObservers) {
      await slideshowAfterUpdate.refreshSlideObservers({ preserveSelection: true });
    }
    if (generation !== this.#galleryUpdateGeneration) {
      onComplete?.();
      return;
    }

    onComplete?.();
  }

  /**
   * @param {boolean} useConnectedGallery
   * @param {HTMLInputElement} selectedOption
   */
  async #finalizeVariantImageSlideSelection(useConnectedGallery, selectedOption) {
    const slideshow = this.#resolveSlideshow();
    if (!slideshow || !selectedOption) return;

    const selectedImageId = selectedOption.dataset.optionMediaId;
    const previewSrc = selectedOption.dataset.previewImageSrc;
    const matchingSlide = selectedImageId
      ? this.#findSlideshowSlideByMediaId(slideshow, selectedImageId)
      : null;

    if (matchingSlide && selectedImageId) {
      if (!useConnectedGallery) {
        const slides = slideshow.refs?.slides ?? [];
        for (const slide of slides) {
          if (slide.getAttribute('variant-image') == null) continue;

          slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
        }
      }

      await this.#trySelectOrPreviewMedia(selectedImageId, previewSrc);
      return;
    }

    if (previewSrc) {
      await this.#trySelectOrPreviewMedia(undefined, previewSrc);
    }
  }

  /** @param {string | undefined} mediaId @param {string | undefined} previewSrc @returns {Promise<boolean>} */
  async #trySelectOrPreviewMedia(mediaId, previewSrc) {
    const slideshow = this.#resolveSlideshow();
    if (!slideshow) return false;

    this.#clearCardGalleryPreview();

    if (mediaId) {
      const slide = this.#findSlideshowSlideByMediaId(slideshow, mediaId);
      if (slide && slideshow.select) {
        await slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
        return true;
      }

      const scroller = slideshow.refs?.scroller;
      if (scroller && slideshow.select) {
        const visibleSlides = Array.from(scroller.querySelectorAll('slideshow-slide')).filter(
          (el) => el instanceof HTMLElement && !el.hidden
        );
        const index = visibleSlides.findIndex((s) => s.getAttribute('slide-id') === String(mediaId));
        if (index >= 0) {
          await slideshow.select(index, undefined, { animate: false });
          return true;
        }
      }
    }

    if (previewSrc) {
      this.#applyCardGalleryPreviewFromUrl(previewSrc);
      return true;
    }

    return false;
  }


  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component') ?? this.querySelector('variant-picker');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#galleryUpdateGeneration++;
      this.#previousSlideIndex = event.detail.index;
      return;
    }

    if (this.#cardGalleryHoverActive || this.#cardGallerySwatchHoverActive) return;

    const selected = this.#cardGallerySwatchSourceInput();
    if (!(selected instanceof HTMLInputElement)) return;

    const featuredId = selected.dataset.optionMediaId?.trim();
    const selectedId = event.detail.id != null ? String(event.detail.id) : '';

    if (featuredId && selectedId && selectedId !== featuredId) {
      const connected = (selected.dataset.connectedProductUrl || '').trim();
      const payload = (selected.dataset.connectedCardGallery || '').trim();
      if (!connected || !payload) return;

      requestAnimationFrame(() => this.#forceFeaturedAtRest(selected));
    }
  };

  /**
   * @param {PointerEvent} event
   */
  previewCardGalleryHover(event) {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label');
    if (!label) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    const mediaId = input.dataset.optionMediaId;
    const previewSrc = input.dataset.previewImageSrc;
    if (!mediaId && !previewSrc) {
      return;
    }

    const slideshow = this.#resolveSlideshow();
    if (!slideshow) return;

    this.resetVariant.cancel();

    this.#cardGallerySwatchHoverActive = true;
    this.#cardGalleryHoveredInput = input;

    const connected = (input.dataset.connectedProductUrl || '').trim();
    const payload = (input.dataset.connectedCardGallery || '').trim();
    const useConnectedGallery = Boolean(connected && payload);

    this.#discardCardGalleryPreviewBackup();

    if (useConnectedGallery) {
      const payloadChanged = this.#syncConnectedCardGalleryPayload(payload);
      this.#afterConnectedGalleryPayload(
        () => this.#trySelectOrPreviewMedia(mediaId, previewSrc),
        payloadChanged
      );
      return;
    }

    this.#trySelectOrPreviewMedia(mediaId, previewSrc);
  }

  /**
   * @param {string | number} id
   */
  previewVariant(id) {
    const slideshow = this.#resolveSlideshow();

    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#trySelectOrPreviewMedia(String(id), undefined);
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#cardGalleryHoverActive) return;

    const slideshow = this.#resolveSlideshow();

    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#cardGalleryHoverActive = true;

    const selected = this.#cardGallerySwatchSourceInput();
    const hoverMediaId = selected?.dataset.galleryHoverMediaId;
    const hoverImageSrc = selected?.dataset.galleryHoverImageSrc;
    const connected = (selected?.dataset.connectedProductUrl || '').trim();
    const payload = (selected?.dataset.connectedCardGallery || '').trim();

    if (selected && connected && payload && (hoverMediaId || hoverImageSrc)) {
      this.#discardCardGalleryPreviewBackup();
      this.#ensureCardGalleryBaselineCaptured();
      const payloadChanged = this.#syncConnectedCardGalleryPayload(payload);
      this.#afterConnectedGalleryPayload(() => {
        this.#trySelectOrPreviewMedia(hoverMediaId || undefined, hoverImageSrc || undefined);
        setTimeout(() => this.#preloadNextPreviewImage());
      }, payloadChanged);
      return;
    }

    if (hoverMediaId || hoverImageSrc) {
      this.#trySelectOrPreviewMedia(hoverMediaId || undefined, hoverImageSrc || undefined);
      setTimeout(() => this.#preloadNextPreviewImage());
      return;
    }

    const slides = slideshow.slides ?? [];

    if (slides.length < 2) {
      return;
    }

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      const idx = Math.min(this.#previousSlideIndex, slides.length - 1);
      if (idx !== slideshow.current) {
        const targetId = slides[idx]?.getAttribute('slide-id');
        if (targetId) {
          this.#trySelectOrPreviewMedia(targetId, undefined);
        } else {
          slideshow.select(idx, undefined, { animate: false });
        }
        setTimeout(() => this.#preloadNextPreviewImage());
        return;
      }
    }

    const nextIdx = (slideshow.current + 1) % slides.length;
    const toId = slides[nextIdx]?.getAttribute('slide-id');
    if (toId) {
      this.#trySelectOrPreviewMedia(toId, undefined);
    } else {
      slideshow.select(nextIdx, undefined, { animate: false });
    }
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#isSpuriousGalleryPointerLeave(event)) return;

    this.#cardGalleryHoverActive = false;

    const slideshow = this.#resolveSlideshow();
    if (!slideshow) return;

    const selected = this.#cardGallerySwatchSourceInput();
    if (selected instanceof HTMLInputElement) {
      this.#forceFeaturedAtRest(selected);
      if (typeof slideshow.select === 'function') {
        void slideshow.select(0, undefined, { animate: false });
      }
      return;
    }

    if (!this.variantPicker) {
      const slides = slideshow.slides ?? [];
      if (slides.length < 2) return;
      const slideId = slideshow.initialSlide?.getAttribute('slide-id');
      if (slideId) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
      }
      return;
    }

    this.#resetVariant();
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    if (this.#cardGalleryHoverActive) return;

    this.#cardGallerySwatchHoverActive = false;

    const slideshow = this.#resolveSlideshow();

    if (!slideshow) return;

    const selectedOption = this.#cardGallerySwatchSourceInput();
    if (selectedOption) {
      this.#updateVariantImages();
      return;
    }

    this.#discardCardGalleryPreviewBackup();

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    slideshow.previous(undefined, { animate: false });
  };

  /**
   * @param {string} url
   */
  #applyCardGalleryPreviewFromUrl(url) {
    const slideshow = this.#resolveSlideshow();
    if (!slideshow || !url) return;

    const slide = slideshow.slides?.[slideshow.current];
    const img = slide?.querySelector('img');
    if (!img) return;

    if (!this.#cardGalleryPreviewBackup) {
      this.#cardGalleryPreviewBackup = { img, src: img.currentSrc || img.src, srcset: img.getAttribute('srcset') };
    }

    img.removeAttribute('srcset');
    img.src = url;
  }

  #clearCardGalleryPreview() {
    if (!this.#cardGalleryPreviewBackup) return;

    const { img, src, srcset } = this.#cardGalleryPreviewBackup;
    if (img.isConnected) {
      img.src = src;
      if (srcset) img.setAttribute('srcset', srcset);
      else img.removeAttribute('srcset');
    }

    this.#cardGalleryPreviewBackup = null;
  }

  #discardCardGalleryPreviewBackup() {
    this.#cardGalleryPreviewBackup = null;
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   * Clicks anywhere inside the options block (including non-interactive gaps)
   * must not trigger card navigation.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const inOptionsBlock =
      Boolean(event.target.closest('.options-block')) ||
      path.some((node) => node instanceof Element && node.classList?.contains('options-block'));

    if (inOptionsBlock) {
      return;
    }

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Card Options split swatches (`swatches-variant-picker-component`) and the rest (`variant-picker`).
   * Requests must include every selected option value; the base getter only sees this subtree, so swatch clicks omitted Size etc.
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    const card = this.closest('product-card');
    const mainPicker = card?.querySelector('variant-picker');

    /** @param {HTMLInputElement | HTMLOptionElement} el */
    const entryFrom = (el) => {
      const { optionValueId, inputId } = el.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      let pos = Number.NaN;
      if (inputId) {
        const positionPart = inputId.split('-')[0];
        if (positionPart !== undefined) pos = Number.parseInt(positionPart, 10);
      }
      return { id: optionValueId, pos: Number.isNaN(pos) ? 999 : pos };
    };

    const fromSwatches = Array.from(this.querySelectorAll('fieldset input:checked')).map((el) =>
      entryFrom(/** @type {HTMLInputElement} */ (el))
    );

    if (!mainPicker) return fromSwatches.map((e) => e.id);

    const fromMain = Array.from(
      mainPicker.querySelectorAll(
        'select option[selected], fieldset:not(.variant-option--swatches):not([data-erp-masked-option="true"]) input:checked'
      )
    ).map((el) => entryFrom(/** @type {HTMLInputElement | HTMLOptionElement} */ (el)));

    return [...fromSwatches, ...fromMain]
      .sort((a, b) => a.pos - b.pos)
      .map((e) => e.id);
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const targetInput = event.target;
    const isSwatchInput =
      targetInput instanceof HTMLInputElement &&
      targetInput.type === 'radio' &&
      (targetInput.name?.includes('-swatch') || Boolean(targetInput.closest('fieldset.variant-option--swatches')));
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      const featuredSection = this.closest('featured-product-information');
      if (featuredSection instanceof HTMLElement && featuredSection.id) {
        url.searchParams.set('section_id', featuredSection.id);
      } else if (this.closest('quick-add-component') || this.closest('quick-add-dialog')) {
        url.searchParams.set('section_id', 'section-rendering-product-card');
      } else if (this.closest('product-card')) {
        url.searchParams.set('section_id', 'section-rendering-product-card');
      }

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
